import type { ReviewCounts } from "../../model/caseReview";
import { REVIEW_DECISIONS } from "../../constants/caseReviewDecisions";
import { aiReviewed, aiToReview, hasFollowupWork, requiredFilled, requiredLeft } from "../../utils/reviewCounts";
import {
  REVIEW_COUNT_ACCEPTED,
  REVIEW_COUNT_AI_HEADING,
  REVIEW_COUNT_ALL_DONE,
  REVIEW_COUNT_CORRECTED,
  REVIEW_COUNT_FOLLOWUP_HEADING,
  REVIEW_COUNT_MISSING_SHOWN,
  REVIEW_COUNT_NOTHING,
  REVIEW_COUNT_RECORDS_HEADING,
  REVIEW_COUNT_REJECTED,
  REVIEW_COUNT_RECORDS_WHERE,
  REVIEW_COUNT_REQUIRED_HEADING,
  REVIEW_COUNT_MANUAL_ENTRY,
  REVIEW_COUNT_TO_REVIEW,
  reviewCountAiLeft,
  reviewCountManualLeft,
  reviewCountDecided,
  reviewCountFilled,
  reviewCountFollowupLeft,
  REVIEW_COUNT_MISSING_PREFIX,
  reviewCountMoreMissing,
  reviewCountRecordsLeft,
  reviewCountRequiredEmpty,
  reviewCountResolved,
  reviewCountReviewed,
} from "../../constants/caseReviewLabels";

/** The one-line state of a scope: the most urgent thing left, or that nothing is. */
export function reviewCountStatus(counts: ReviewCounts): string {
  const left = requiredLeft(counts);
  if (left) return reviewCountRequiredEmpty(left);
  const toReview = aiToReview(counts);
  if (toReview) return reviewCountAiLeft(toReview);
  if (counts.ai.manual) return reviewCountManualLeft(counts.ai.manual);
  const unresolved = counts.followup ? counts.followup.records.total - counts.followup.records.resolved : 0;
  if (unresolved) return reviewCountRecordsLeft(unresolved);
  const pending = counts.followup ? counts.followup.total - counts.followup.decided : 0;
  if (pending) return reviewCountFollowupLeft(pending);
  return hasAnything(counts) ? REVIEW_COUNT_ALL_DONE : REVIEW_COUNT_NOTHING;
}

/** The card's numbers as one sentence, without a name; empty when the scope has nothing to count. */
export function reviewCountFacts(counts: ReviewCounts): string {
  const parts: string[] = [];
  if (counts.required.total) parts.push(`${REVIEW_COUNT_REQUIRED_HEADING}: ${reviewCountFilled(requiredFilled(counts), counts.required.total)}`);
  if (counts.ai.total) parts.push(`${REVIEW_COUNT_AI_HEADING}: ${reviewCountReviewed(aiReviewed(counts), counts.ai.total)}`);
  if (counts.ai.manual) parts.push(reviewCountManualLeft(counts.ai.manual));
  if (counts.followup?.records.total) parts.push(`${REVIEW_COUNT_RECORDS_HEADING}: ${reviewCountResolved(counts.followup.records.resolved, counts.followup.records.total)}`);
  if (counts.followup?.total) parts.push(`${REVIEW_COUNT_FOLLOWUP_HEADING}: ${reviewCountDecided(counts.followup.decided, counts.followup.total)}`);
  return parts.join(". ");
}

/** A scope's name and its numbers — what a screen reader hears where only the count names the scope. */
export function reviewCountSummary(label: string, counts: ReviewCounts): string {
  const facts = reviewCountFacts(counts);
  return facts ? `${label}, ${facts}` : label;
}

function hasAnything(counts: ReviewCounts): boolean {
  return counts.required.total > 0 || counts.ai.total > 0 || counts.ai.manual > 0 || hasFollowupWork(counts.followup);
}

function Bar({ parts, total }: { parts: Array<{ value: number; tone: string }>; total: number }) {
  return <span className="cr-count-bar" aria-hidden="true">
    {parts.filter((part) => part.value > 0).map((part) => <span key={part.tone} className="cr-count-bar-part" data-tone={part.tone}
      style={{ width: `${(part.value / total) * 100}%` }} />)}
  </span>;
}

function Block({ heading, figure, children }: { heading: string; figure: string; children: React.ReactNode }) {
  return <div className="cr-count-card-block">
    <div className="cr-count-card-line"><span className="cr-count-card-heading">{heading}</span><span className="cr-count-card-figure">{figure}</span></div>
    {children}
  </div>;
}

/**
 * The details of one scope's counts. Presentational: counts in, markup out; informational only.
 *
 * <p>A block is left out when the scope has nothing of that kind, so a card never shows "0 of 0".
 */
export function ReviewCountDetails({ label, counts }: { label: string; counts: ReviewCounts }) {
  const left = requiredLeft(counts);
  const missing = counts.required.missing;
  const shown = missing.slice(0, REVIEW_COUNT_MISSING_SHOWN);
  const toReview = aiToReview(counts);
  return <div className="cr-count-card">
    <div className="cr-count-card-head">
      <span className="cr-count-card-title">{label}</span>
      <span className="cr-count-card-status" data-tone={left ? "danger" : "quiet"}>{reviewCountStatus(counts)}</span>
    </div>
    {counts.required.total > 0 && <Block heading={REVIEW_COUNT_REQUIRED_HEADING}
      figure={reviewCountFilled(requiredFilled(counts), counts.required.total)}>
      <Bar total={counts.required.total} parts={[{ value: requiredFilled(counts), tone: "done" }]} />
      {left > 0 && <p className="cr-count-card-missing">
        <span>{REVIEW_COUNT_MISSING_PREFIX}</span>{" "}
        {shown.map((gap) => gap.label ?? gap.fieldId).join(", ")}
        {missing.length > shown.length && ` ${reviewCountMoreMissing(missing.length - shown.length)}`}
      </p>}
    </Block>}
    {(counts.ai.total > 0 || counts.ai.manual > 0) && <Block heading={REVIEW_COUNT_AI_HEADING}
      figure={counts.ai.total > 0 ? reviewCountReviewed(aiReviewed(counts), counts.ai.total) : reviewCountManualLeft(counts.ai.manual)}>
      {counts.ai.total > 0 && <Bar total={counts.ai.total} parts={[
        { value: counts.ai.accepted, tone: REVIEW_DECISIONS.accepted },
        { value: counts.ai.corrected, tone: REVIEW_DECISIONS.corrected },
        { value: counts.ai.rejected, tone: REVIEW_DECISIONS.rejected }]} />}
      <ul className="cr-count-legend">
        <li data-tone={REVIEW_DECISIONS.accepted}>{REVIEW_COUNT_ACCEPTED} {counts.ai.accepted}</li>
        <li data-tone={REVIEW_DECISIONS.corrected}>{REVIEW_COUNT_CORRECTED} {counts.ai.corrected}</li>
        <li data-tone={REVIEW_DECISIONS.rejected}>{REVIEW_COUNT_REJECTED} {counts.ai.rejected}</li>
        <li data-tone="pending">{REVIEW_COUNT_TO_REVIEW} {toReview}</li>
        {counts.ai.manual > 0 && <li data-tone="manual">{REVIEW_COUNT_MANUAL_ENTRY} {counts.ai.manual}</li>}
      </ul>
    </Block>}
    {counts.followup && counts.followup.records.total > 0 && <Block heading={REVIEW_COUNT_RECORDS_HEADING}
      figure={reviewCountResolved(counts.followup.records.resolved, counts.followup.records.total)}>
      <Bar total={counts.followup.records.total} parts={[{ value: counts.followup.records.resolved, tone: "followup" }]} />
      {counts.followup.records.resolved < counts.followup.records.total
        && <p className="cr-count-card-missing">{REVIEW_COUNT_RECORDS_WHERE}</p>}
    </Block>}
    {counts.followup && counts.followup.total > 0 && <Block heading={REVIEW_COUNT_FOLLOWUP_HEADING}
      figure={reviewCountDecided(counts.followup.decided, counts.followup.total)}>
      <Bar total={counts.followup.total} parts={[{ value: counts.followup.decided, tone: "followup" }]} />
    </Block>}
  </div>;
}
